import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../lib/utils.js';

const buttonVariants = cva(
  /* `transition-all` + `active:scale-[0.98]` sinalizam o estado pressionado;
     `duration-100` evita lag perceptível na animação. */
  'relative inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-100 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 select-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent/10 hover:text-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent/15 hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  readonly asChild?: boolean;
  /**
   * Quando `true`, o botão exibe um spinner sobre o conteúdo e fica
   * `disabled` automaticamente. O texto interno permanece no DOM com
   * `visibility: hidden`, preservando a largura original — evita "salto"
   * visual ao trocar entre estados idle ↔ loading.
   *
   * Padrão recomendado pelo design system: NUNCA troque o label do
   * botão por "Salvando…"/"Processando…" — use `loading={true}` para
   * manter a forma estável.
   */
  readonly loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, children, disabled, ...props },
    ref,
  ) => {
    /* `asChild` cede o elemento ao filho (Slot Radix). Loading state com
       `asChild` não faz sentido (o filho controla o markup) — fallback
       silencioso para o comportamento simples sem spinner overlay. */
    if (asChild) {
      const Comp = Slot;
      return (
        <Comp
          ref={ref}
          data-slot="button"
          className={cn(buttonVariants({ variant, size, className }))}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    const isDisabled = disabled === true || loading;
    return (
      <button
        ref={ref}
        data-slot="button"
        data-loading={loading ? 'true' : undefined}
        className={cn(buttonVariants({ variant, size, className }))}
        type={props.type ?? 'button'}
        disabled={isDisabled}
        aria-busy={loading ? 'true' : undefined}
        {...props}
      >
        {loading ? (
          <>
            <span className="absolute inset-0 inline-flex items-center justify-center">
              <Loader2Icon className="animate-spin" aria-hidden="true" />
            </span>
            <span className="invisible inline-flex items-center gap-2" aria-hidden="true">
              {children}
            </span>
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
